import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  setSelectedElement,
  setReadOnlyElement,
} from '../../actions';
import {
  MENU_ITEM__QUESTIONNAIRES__AVAILABLE,
  MENU_ITEM__FORMS__ASSIGNED,
  MENU_ITEM__FORMS__DRAFT,
  MENU_ITEM__FORMS__RETURNED,
  MENU_ITEM__FORMS__SENT,
  MENU_ITEM__FORMS__APPROVED,
} from '../../constants';

import FillButtonBlueImage from '../../../assets/icons/fill-blue.svg';
import FillButtonNavyImage from '../../../assets/icons/fill-navy.svg';
import InformationNavyImage from '../../../assets/icons/info-circle-navy.svg';
import Loader from '../../../shared/components/loader';
import FormNotification from '../../../shared/components/formNotification';

import AdditionalFormFields from './additionalFormFields';
import { informationTextMessage } from './helpers/questionnaires';

const Questionnaires = ({
  items,
  isFetching,
  message,
}) => {
  const dispatch = useDispatch();

  const selectedMenuItem = useSelector((store) => store.authorizedState.get('selectedMenuItem'));

  const fillButtonClick = (id) => {
    dispatch(setSelectedElement(id));
    dispatch(setReadOnlyElement(false));
  };

  const previewButtonClick = (id) => {
    dispatch(setSelectedElement(id));
    dispatch(setReadOnlyElement(true));
  };

  const [isFillButtonHover, setIsFillButtonHover] = useState(false);
  const isPreviewButtonAvailable = useMemo(() => ![
    MENU_ITEM__QUESTIONNAIRES__AVAILABLE,
  ].includes(selectedMenuItem), [selectedMenuItem]);

  const isFillButtonAvailable = useMemo(() => [
    MENU_ITEM__FORMS__ASSIGNED,
    MENU_ITEM__FORMS__DRAFT,
    MENU_ITEM__FORMS__RETURNED,
    MENU_ITEM__QUESTIONNAIRES__AVAILABLE,
  ].includes(selectedMenuItem), [selectedMenuItem]);

  return (
    <div className="questionnaires">
      <If condition={message.value}>
        <FormNotification message={message} />
      </If>
      <div className="questionnaires-header">
        <div className="type">
          <Choose>
            <When condition={
              selectedMenuItem === MENU_ITEM__QUESTIONNAIRES__AVAILABLE
            }
            >
              Twoje ankiety /
            </When>
            <When condition={
              selectedMenuItem === MENU_ITEM__FORMS__ASSIGNED
              || selectedMenuItem === MENU_ITEM__FORMS__DRAFT
              || selectedMenuItem === MENU_ITEM__FORMS__RETURNED
              || selectedMenuItem === MENU_ITEM__FORMS__SENT
              || selectedMenuItem === MENU_ITEM__FORMS__APPROVED
            }
            >
              Twoje formularze /
            </When>
          </Choose>
        </div>
        <div className="title">
          <Choose>
            <When condition={selectedMenuItem === MENU_ITEM__QUESTIONNAIRES__AVAILABLE}>
              Dostępne ankiety
            </When>
            <When condition={selectedMenuItem === MENU_ITEM__FORMS__ASSIGNED}>
              Przydzielone formularze
            </When>
            <When condition={selectedMenuItem === MENU_ITEM__FORMS__DRAFT}>
              Robocze formularze
            </When>
            <When condition={selectedMenuItem === MENU_ITEM__FORMS__RETURNED}>
              Zwrócone formularze
            </When>
            <When condition={selectedMenuItem === MENU_ITEM__FORMS__SENT}>
              Wysłane formularze
            </When>
            <When condition={selectedMenuItem === MENU_ITEM__FORMS__APPROVED}>
              Zatwierdzone formularze

              <img
                className="icon tooltip-container"
                src={InformationNavyImage}
                alt={informationTextMessage}
                title={informationTextMessage}
              />
            </When>
          </Choose>
        </div>
      </div>
      <div className="questionnaires-content">
        <Choose>
          <When condition={isFetching}>
            <Loader />
          </When>
          <When condition={items && items.length > 0}>
            <For each="item" index="id" of={items}>
              <div
                key={item.id}
                className="item"
              >
                <div className="content">
                  <div className="title">
                    {item.title}
                  </div>
                  <div className="description">
                    {item.description}
                  </div>
                  <AdditionalFormFields
                    form={item}
                    selectedMenuItem={selectedMenuItem}
                  />
                </div>
                <If condition={isPreviewButtonAvailable}>
                  <div
                    className="button"
                    onMouseEnter={() => setIsFillButtonHover(true)}
                    onMouseLeave={() => setIsFillButtonHover(false)}
                    onClick={() => previewButtonClick(item.id)}
                    role="button"
                    title="Podglad"
                  >
                    <img className="icon" src={isFillButtonHover ? FillButtonBlueImage : FillButtonNavyImage} alt="Podgląd" />
                    Podgląd
                  </div>
                </If>
                <Choose>
                  <When condition={isFillButtonAvailable}>
                    <div
                      className="button"
                      onMouseEnter={() => setIsFillButtonHover(true)}
                      onMouseLeave={() => setIsFillButtonHover(false)}
                      onClick={() => fillButtonClick(item.id)}
                      role="button"
                      title="Wypełnij"
                    >
                      <img className="icon" src={isFillButtonHover ? FillButtonBlueImage : FillButtonNavyImage} alt="Wypełnij" />
                      Wypełnij
                    </div>
                  </When>
                </Choose>
              </div>
            </For>
          </When>
          <Otherwise>
            <div className="no-content-wrapper">Brak elementów do wyświetlenia</div>
          </Otherwise>
        </Choose>
      </div>
    </div>
  );
};

Questionnaires.propTypes = {
  // Ankiety.
  items: PropTypes.arrayOf(
    PropTypes.shape({
      // Identyfikator.
      id: PropTypes.string.isRequired,
      // Tytuł.
      title: PropTypes.string.isRequired,
    }),
  ),
  isFetching: PropTypes.bool,
  // Notyfikacje
  message: PropTypes.shape({
    value: PropTypes.string,
    type: PropTypes.string,
  }),
};

Questionnaires.defaultProps = {
  items: [],
  isFetching: false,
  message: {},
};

export default Questionnaires;
